/**
 * تتبّع أحداث تثبيت PWA — استقبال خفيف من المتصفّح + استعلام للأدمن.
 * الإدراج public لأنّه يجمع إشارات تثبيت من أيّ زائر (لا بيانات حسّاسة).
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const EVENT_TYPES = [
  "prompt_available",
  "prompt_accepted",
  "prompt_dismissed",
  "installed",
  "already_installed",
  "launched_standalone",
  "card_viewed",
  "cta_clicked",
  "ios_hint_opened",
] as const;

const LogInput = z.object({
  eventType: z.enum(EVENT_TYPES),
  sessionId: z.string().min(1).max(128).optional(),
  userAgent: z.string().max(500).optional(),
  platform: z.string().max(64).optional(),
  locale: z.string().max(8).optional(),
  market: z.string().max(8).optional(),
  displayMode: z.string().max(32).optional(),
  meta: z.record(z.string().max(64), z.union([z.string(), z.number(), z.boolean()])).optional(),
});

export const logPwaInstallEvent = createServerFn({ method: "POST" })
  .inputValidator((input) => LogInput.parse(input))
  .handler(async ({ data }) => {
    // محاولة جلب user_id إن كان متوفّراً (لا نطلبه)
    let userId: string | null = null;
    try {
      // optimistic: serverFn يحمل auth header غالباً عبر attachSupabaseAuth
      const auth = await supabaseAdmin.auth.getUser();
      userId = auth.data.user?.id ?? null;
    } catch {
      userId = null;
    }

    await supabaseAdmin.from("pwa_install_events").insert({
      event_type: data.eventType,
      user_id: userId,
      session_id: data.sessionId ?? null,
      user_agent: data.userAgent ?? null,
      platform: data.platform ?? null,
      locale: data.locale ?? null,
      market: data.market ?? null,
      display_mode: data.displayMode ?? null,
      meta: data.meta ?? {},
    });

    return { ok: true };
  });

/**
 * إحصاءات للأدمن — عدّ الأحداث آخر N يوم، مجمَّعة بالنوع + المنصّة.
 */
export const getPwaInstallStats = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { days?: number }) => ({
    days: Math.max(1, Math.min(365, Math.floor(input?.days ?? 30))),
  }))
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const { data: roles } = await supabaseAdmin
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);
    const isAdmin = (roles ?? []).some((r) => r.role === "admin");
    if (!isAdmin) throw new Error("Forbidden");

    const since = new Date(Date.now() - data.days * 24 * 60 * 60 * 1000).toISOString();

    const { data: rows, error } = await supabaseAdmin
      .from("pwa_install_events")
      .select("event_type, platform, locale, market, created_at")
      .gte("created_at", since)
      .order("created_at", { ascending: false })
      .limit(5000);

    if (error) throw error;

    const byType: Record<string, number> = {};
    const byPlatform: Record<string, number> = {};
    const byMarket: Record<string, number> = {};
    for (const r of rows ?? []) {
      byType[r.event_type] = (byType[r.event_type] ?? 0) + 1;
      const p = r.platform ?? "unknown";
      byPlatform[p] = (byPlatform[p] ?? 0) + 1;
      const m = r.market ?? "unknown";
      byMarket[m] = (byMarket[m] ?? 0) + 1;
    }

    const prompted = byType.prompt_available ?? 0;
    const accepted = byType.prompt_accepted ?? 0;
    const dismissed = byType.prompt_dismissed ?? 0;
    const installed = byType.installed ?? 0;
    const launched = byType.launched_standalone ?? 0;
    const acceptanceRate = prompted > 0 ? Math.round((accepted / prompted) * 100) : 0;

    return {
      windowDays: data.days,
      totals: { prompted, accepted, dismissed, installed, launched },
      acceptanceRate,
      byType,
      byPlatform,
      byMarket,
      recent: (rows ?? []).slice(0, 50),
    };
  });
